import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    Container,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Stack,
    Typography,
    Alert,
    Snackbar,
    FormHelperText,
    Paper
} from '@mui/material';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'utils/axios';
import { useTranslation } from 'react-i18next';

async function verifyResetToken(uid, token) {
    const res = await axios.get(`password/verify/${uid}/${token}/`);
    if (res.status !== 200) return { valid: false };
    return res.data;
}

async function submitNewPassword(uid, token, new_password, confirm_password) {
    const res = await axios.post(`password/reset/`, {
        uid,
        token,
        new_password,
        confirm_password
    });
    return res;
}

// ---- Password helpers ----
const hasLowercase = (s) => /[a-z]/.test(s);
const hasUppercase = (s) => /[A-Z]/.test(s);
const hasNumber = (s) => /\d/.test(s);
const hasSpecial = (s) => /[^A-Za-z0-9]/.test(s);

export default function ResetPassword() {
    const { t } = useTranslation();
    const { uid, token } = useParams();
    const navigate = useNavigate();

    // Yup validation to enforce the same rules
    const validationSchema = Yup.object({
        new_password: Yup.string()
            .min(9, t('auth.reset_password.validation.min'))
            .test('lower', t('auth.reset_password.validation.lowercase'), (v) => !!v && hasLowercase(v))
            .test('upper', t('auth.reset_password.validation.uppercase'), (v) => !!v && hasUppercase(v))
            .test('number', t('auth.reset_password.validation.number'), (v) => !!v && hasNumber(v))
            .test('special', t('auth.reset_password.validation.special'), (v) => !!v && hasSpecial(v))
            .required(t('auth.reset_password.validation.required')),
        confirm_password: Yup.string()
            .oneOf([Yup.ref('new_password')], t('auth.reset_password.validation.mismatch'))
            .required(t('auth.reset_password.validation.confirm_required'))
    });

    const [checking, setChecking] = useState(true);
    const [valid, setValid] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [snack, setSnack] = useState(null);

    useEffect(() => {
        let cancelled = false;
        async function run() {
            if (!uid || !token) return; // wait for router hydration
            setChecking(true);
            try {
                const result = await verifyResetToken(uid, token);
                if (!cancelled) setValid(!!result.valid);
            } catch (e) {
                if (!cancelled) setValid(false);
            } finally {
                if (!cancelled) setChecking(false);
            }
        }
        run();
        return () => {
            cancelled = true;
        };
    }, [uid, token]);

    return (
        <>
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Paper elevation={1} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
                    <Stack spacing={2}>
                        <Typography variant="h4" fontWeight={700}>
                            {t('auth.reset_password.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('auth.reset_password.description')}
                        </Typography>

                        {checking && (
                            <Box display="flex" alignItems="center" gap={2} mt={1}>
                                <CircularProgress size={20} />
                                <Typography variant="body2">{t('auth.reset_password.validating')}</Typography>
                            </Box>
                        )}

                        {!checking && valid === false && (
                            <Alert severity="error">
                                {t('auth.reset_password.invalid_link')} <strong>{t('auth.reset_password.invalid_link_bold')}</strong>{' '}
                                {t('auth.reset_password.invalid_link_end')}
                            </Alert>
                        )}

                        {!checking && valid && (
                            <Formik
                                initialValues={{ new_password: '', confirm_password: '' }}
                                validationSchema={validationSchema}
                                onSubmit={async (values, { setSubmitting }) => {
                                    if (!uid || !token) return;
                                    const res = await submitNewPassword(uid, token, values.new_password, values.confirm_password);
                                    setSubmitting(false);
                                    if (res.status === 200) {
                                        setSnack({ open: true, message: t('auth.reset_password.success_message'), severity: 'success' });
                                        setTimeout(() => navigate('/login'), 1200);
                                    } else {
                                        setSnack({ open: true, message: res.data.message, severity: 'error' });
                                    }
                                }}
                            >
                                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => {
                                    const pw = values.new_password || '';
                                    return (
                                        <form onSubmit={handleSubmit} noValidate>
                                            <Stack spacing={2.5}>
                                                <Box>
                                                    <InputLabel htmlFor="new-password">{t('auth.reset_password.new_password')}</InputLabel>
                                                    <OutlinedInput
                                                        id="new-password"
                                                        name="new_password"
                                                        type={showPassword ? 'text' : 'password'}
                                                        value={values.new_password}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        fullWidth
                                                        error={Boolean(touched.new_password && errors.new_password)}
                                                        placeholder={t('auth.reset_password.new_password_placeholder')}
                                                        endAdornment={
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => setShowPassword((s) => !s)}
                                                                    edge="end"
                                                                    aria-label={t('auth.reset_password.toggle_password')}
                                                                >
                                                                    {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        }
                                                    />
                                                    {touched.new_password && errors.new_password && (
                                                        <FormHelperText error>{errors.new_password}</FormHelperText>
                                                    )}
                                                </Box>

                                                <Box>
                                                    <InputLabel htmlFor="confirm-password">
                                                        {t('auth.reset_password.confirm_password')}
                                                    </InputLabel>
                                                    <OutlinedInput
                                                        id="confirm-password"
                                                        name="confirm_password"
                                                        type={showConfirm ? 'text' : 'password'}
                                                        value={values.confirm_password}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        fullWidth
                                                        error={Boolean(touched.confirm_password && errors.confirm_password)}
                                                        placeholder={t('auth.reset_password.confirm_password_placeholder')}
                                                        endAdornment={
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => setShowConfirm((s) => !s)}
                                                                    edge="end"
                                                                    aria-label={t('auth.reset_password.toggle_confirm')}
                                                                >
                                                                    {showConfirm ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        }
                                                    />
                                                    {touched.confirm_password && errors.confirm_password && (
                                                        <FormHelperText error>{errors.confirm_password}</FormHelperText>
                                                    )}
                                                </Box>

                                                {/* Live password rules checklist */}
                                                <Box
                                                    component="ul"
                                                    sx={{
                                                        mt: 1.5,
                                                        pl: 3,
                                                        color: 'text.secondary',
                                                        '& li': { lineHeight: 1.7 },
                                                        '& li.checked': { color: 'success.main', fontWeight: 600 }
                                                    }}
                                                >
                                                    <li className={pw.length > 8 ? 'checked' : ''}>
                                                        {t('auth.reset_password.password_rules.min')}
                                                    </li>
                                                    <li className={hasLowercase(pw) ? 'checked' : ''}>
                                                        {t('auth.reset_password.password_rules.lowercase')}
                                                    </li>
                                                    <li className={hasUppercase(pw) ? 'checked' : ''}>
                                                        {t('auth.reset_password.password_rules.uppercase')}
                                                    </li>
                                                    <li className={hasNumber(pw) ? 'checked' : ''}>
                                                        {t('auth.reset_password.password_rules.number')}
                                                    </li>
                                                    <li className={hasSpecial(pw) ? 'checked' : ''}>
                                                        {t('auth.reset_password.password_rules.special')}
                                                    </li>
                                                </Box>
                                                <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                                                    {isSubmitting
                                                        ? t('auth.reset_password.updating')
                                                        : t('auth.reset_password.reset_button')}
                                                </Button>
                                            </Stack>
                                        </form>
                                    );
                                }}
                            </Formik>
                        )}
                    </Stack>
                </Paper>

                <Snackbar
                    open={!!snack?.open}
                    autoHideDuration={4000}
                    onClose={() => setSnack(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {snack ? (
                        <Alert onClose={() => setSnack(null)} severity={snack.severity} sx={{ width: '100%' }}>
                            {snack.message}
                        </Alert>
                    ) : null}
                </Snackbar>
            </Container>
        </>
    );
}
